import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { articleContent } from '@/mocks/articleData';
import { getArticleBySlug } from '@/mocks/articles-index';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const data = useMemo(() => {
    if (slug === 'oopbuy-spreadsheet-guide-deutschland') {
      return articleContent;
    }
    return getArticleBySlug(slug || '');
  }, [slug]);

  // Inject Schema.org JSON-LD into document head (BEFORE early return!)
  useEffect(() => {
    if (!data) return;

    const canonicalUrl = `https://oopbuyspread.boats/artikel/${data.slug}`;
    const publishedDate = data.publishedDate || '2026-07-16';

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Startseite', item: 'https://oopbuyspread.boats/' },
        { '@type': 'ListItem', position: 2, name: data.category || 'Guide', item: canonicalUrl },
        { '@type': 'ListItem', position: 3, name: data.title, item: canonicalUrl },
      ],
    };

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: data.title,
      description: data.metaDescription,
      image: data.image,
      datePublished: publishedDate,
      author: { '@type': 'Organization', name: 'Oopbuy Spreadsheet Team' },
      publisher: { '@type': 'Organization', name: 'oopbuyspread.boats', logo: { '@type': 'ImageObject', url: 'https://oopbuyspread.boats/logo.png' } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    };

    const faqSection = data.sections?.find((s: { isFAQ?: boolean }) => s.isFAQ);
    const schemas = [breadcrumbSchema, articleSchema];
    if (faqSection) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqSection.faqs?.map((f: { q: string; a: string }) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [slug, data]);

  if (!data) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-24 md:pt-32 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">Artikel nicht gefunden</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">← Zurück zur Startseite</Link>
        </div>
        <FooterSection />
      </div>
    );
  }

  const canonicalUrl = `https://oopbuyspread.boats/artikel/${data.slug}`;
  const publishedDate = data.publishedDate || '2026-07-16';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article header */}
      <header className="pt-24 md:pt-32 pb-12 md:pb-16 bg-background-100/50">
        <div className="max-w-[800px] mx-auto px-6 md:px-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              Startseite
            </Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-400 whitespace-nowrap">{data.category || 'Guide'}</span>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-700 font-medium line-clamp-1">{data.title}</span>
          </nav>

          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            {data.category}
          </span>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
            {data.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {data.readTime} Lesezeit
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {new Date(publishedDate).toLocaleDateString('de-DE', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              Oopbuy Team
            </span>
          </div>
        </div>
      </header>

      {/* Featured image */}
      <div className="max-w-[900px] mx-auto px-6 md:px-10 -mt-4 mb-10">
        <img
          src={data.image}
          alt={data.title}
          className="w-full rounded-lg object-cover object-center"
          style={{ maxHeight: '420px' }}
        />
      </div>

      {/* Article body */}
      <article className="max-w-[800px] mx-auto px-6 md:px-10 pb-16">
        {/* Table of Contents */}
        {data.sections && data.sections.length > 1 && (
          <nav className="bg-background-100/70 rounded-lg p-6 mb-12">
            <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">Inhaltsverzeichnis</h2>
            <ol className="space-y-2">
              {data.sections.map((section: { heading: string }, i: number) => (
                <li key={i} className="text-sm">
                  <a href={`#section-${i}`} className="text-foreground-600 hover:text-primary-600 transition-colors flex items-start gap-2">
                    <span className="text-primary-500 font-semibold min-w-[1.5rem]">{i + 1}.</span>
                    <span>{section.heading}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Sections */}
        {data.sections && data.sections.map((section: { heading: string; isFAQ?: boolean; faqs?: { q: string; a: string }[]; content?: string }, i: number) => (
          <section key={i} id={`section-${i}`} className="mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-5 leading-snug">
              {section.heading}
            </h2>

            {section.isFAQ ? (
              <div className="space-y-4">
                {section.faqs?.map((faq: { q: string; a: string }, j: number) => (
                  <details key={j} className="bg-background-100/70 rounded-lg p-5 group cursor-pointer">
                    <summary className="font-semibold text-foreground-900 text-sm md:text-base list-none flex items-center justify-between gap-4">
                      {faq.q}
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed pl-0">{faq.a}</p>
                  </details>
                ))}
              </div>
            ) : (
              <div className="text-base text-foreground-700 leading-relaxed space-y-4">
                {section.content?.split('\n\n').map((paragraph: string, j: number) => (
                  <p key={j}>{paragraph.trim()}</p>
                ))}
              </div>
            )}
          </section>
        ))}

        {/* Internal linking box */}
        <div className="mt-12 p-6 bg-primary-50/70 rounded-lg border border-primary-200/50">
          <p className="text-sm font-semibold text-primary-800 mb-2">
            <i className="ri-links-line mr-1.5"></i>Verwandte Ressourcen
          </p>
          <ul className="space-y-1.5">
            <li>
              <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                ← Zurück zur Startseite – Alle Kategorien entdecken
              </Link>
            </li>
            <li>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                oopbuy spreadsheet auf kaspreadsheet.com <i className="ri-external-link-line text-xs"></i>
              </a>
            </li>
          </ul>
        </div>

        {/* Category quick links */}
        <div className="mt-10 pt-8 border-t border-background-200/70">
          <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">Alle Oopbuy Spreadsheet Kategorien</h3>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-primary-100 hover:text-primary-700 transition-all whitespace-nowrap cursor-pointer"
              >
                {cat.nameDe}
              </a>
            ))}
          </div>
        </div>
      </article>

      <FooterSection />
    </div>
  );
}